import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import sha512 from "crypto-js/sha512";

import "./../../assets/css/login.scss";

import { useTranslation } from "../../contexts/TranslationContext";
import { useLoginState } from "../../hooks/useLoginState";
import api from "../../utils/api";

const Login = () => {
  const { language } = useTranslation(); // Access the language object
  const [isLoggedIn] = useLoginState(); // Check if user is already logged in
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [capsLockOn, setCapsLockOn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Debug language updates
  useEffect(() => {
    console.log('=== Login component language update ===');
    console.log('Language object:', language);
    console.log('Login title:', language?.loginTitle);
    console.log('Login object:', language?.login);
    console.log('=== End login component language update ===');
  }, [language]);
  
  // Get the intended destination from location state (from ProtectedRoute) or URL search params
  const urlParams = new URLSearchParams(location.search);
  const redirectPath = urlParams.get('redirect');
  const from = location.state?.from?.pathname || redirectPath || "/";
  // Function to detect Caps Lock - only use with keyboard events
  const checkCapsLock = (e) => {
    // Only call getModifierState on keyboard events
    if (e && e.getModifierState) {
      const capsLockOn = e.getModifierState('CapsLock');
      setCapsLockOn(capsLockOn);
    }
  };

  // We only need to check Caps Lock on the password field, not globally
  // No need for the global event listener that's causing the error
  useEffect(() => {
    // Debug language loading
    // console.log('Login component - current language data:', language);
    // console.log('Login component - login text:', language?.login);

    // Cleanup function is empty since we're not adding a global event listener
    return () => { };
  }, [language]);

  // Redirect already logged-in users to gallery
  useEffect(() => {
    console.log('Login useEffect - isLoggedIn:', isLoggedIn);
    console.log('Login useEffect - FWToken in localStorage:', !!localStorage.getItem("FWToken"));
    
    if (isLoggedIn) {
      console.log('User already logged in, redirecting to gallery');
      navigate('/', { replace: true }); // Navigate to index route which is gallery
    }
  }, [isLoggedIn, navigate]);

  // Additional check on mount to handle cases where useLoginState might be slow
  useEffect(() => {
    const token = localStorage.getItem("FWToken");
    if (token) {
      console.log('Direct token check - User is logged in, redirecting to gallery');
      navigate('/', { replace: true });
    }
  }, [navigate]); // Run only on mount

  const handleLogin = async (e) => {
    e.preventDefault();

    // Hash the password using SHA-512
    const hashedPassword = sha512(password).toString();

    try {
      const urlRequest = `${global.config.api.domain}/fwiq-api/api/account/gettoken`;
      const response = await api.post(urlRequest, {
        username: username,
        sha512password: hashedPassword,
      });

      if (response.status === 200 && response.data) {
        const data = response.data;

        if (data.Token) {
          // Store token
          console.log("user", data);

          localStorage.setItem("FWToken", data.Token);
          localStorage.setItem("FWUser", JSON.stringify(data));

          // Dispatch custom login event for the useLoginState hook
          // This will trigger the navbar and other components to update
          window.dispatchEvent(new Event("login"));

          // Navigate to intended destination or gallery page with replace to avoid back button issues
          // Use a small delay to ensure state is updated
          setTimeout(() => {
            navigate(from, { replace: true });
          }, 50);
        } else {
          setError(language.login?.invalidCredentials || "Invalid username or password.");
        }
      } else {
        setError(language.login?.usernameOrPasswordIncorrect || "Username or password is invalid.");
      }
    } catch (err) {
      console.error("Error during login:", err);
      const errMsg = err.response?.data?.message || language.login?.loginError || "Login failed. Please try again.";
      setError(errMsg);
    }
  };

  return (
    <div className="login">
      <h1>{language.login?.loginTitle || "Login"}</h1>

      <div className="login-box">
        <form onSubmit={handleLogin}>
          <div className="login-item">
            <div className="title-item">
              <label htmlFor="username">{language.login?.username || "Username"}</label>
            </div>

            <div className="textbox">
              <input
                type="text"
                id="username"
                tabIndex={1}
                placeholder={language.login?.username || "Username"}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="login-item">
            <div className="title-item inline">
              <label htmlFor="password">{language.login?.password || "Password"}</label>
              {/* Forgot Password Link */}
              <div className="forgot-password">
                <p>
                  <span
                    tabIndex={5}
                    role="button"
                    onClick={() => {
                      return window.open(`https://login.evidenceiq.com/BIQ/login.aspx?forgot_pw=1`, "_blank");
                    }}
                    className="link-button"
                  >
                    {language.login?.forgotPassword || "Forgot Password?"}
                  </span>
                </p>
              </div>
            </div>
            <div className="textbox">
              <input
              type="password"
              id="password"
              tabIndex={2}
              placeholder={language.login?.password || "Password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={checkCapsLock}
              onKeyUp={checkCapsLock}
              onBlur={() => setCapsLockOn(false)}
              onFocus={(e) => checkCapsLock(e)}
              required
            />
              {capsLockOn && (
                <div className="caps-lock-warning">
                  <i className="fa fa-exclamation-triangle"></i>
                  {language.login?.capsLockOn || "Caps Lock is on"}
                </div>
              )}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" tabIndex={2} className="btn btn-login">
            {language.login?.loginButton || "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
